import { copyAssetReferences, type AssetReferenceSource } from '@/api/assetCopy.api';
import { useWorkflowStore } from '@/state/workflow.state';
import { assetIdsIn, remapAssetIds } from './nodeClipboard';

/**
 * Re-references a payload's assets from the destination workflow and returns it under the new ids,
 * so nodes landing from another workflow or a template are inserted already owning their assets.
 * Rejects when the copy fails: an asset id resolves in the workflow that owns it, so the caller
 * must not insert the payload under the source's ids. A ref the destination gets no copy of lands
 * without its Blobstore field.
 */
export async function copyAssetsForInsert<T>(
  source: AssetReferenceSource,
  destinationWorkflowId: string,
  payload: T,
  isCurrent: () => boolean = () => true,
): Promise<T> {
  const assetIds = assetIdsIn(payload);
  if (assetIds.length === 0) return payload;

  const copied = await copyAssetReferences({ source, destinationWorkflowId, assetIds });
  if (!isCurrent()) throw new Error('Asset insertion was cancelled');
  if (assetIds.some((id) => !(id in copied.copiedRefs))) {
    throw new Error('Asset copy returned incomplete destination references');
  }
  useWorkflowStore.getState().mergeResolvedAssets(copied.resolvedAssets);
  return remapAssetIds(payload, copied.copiedRefs);
}
